/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { captureException, setContext, setTag } from "@sentry/nextjs";
import clsx from "clsx";
import merge from "lodash/merge";
import omit from "lodash/omit";
import type {
  AppContext,
  AppInitialProps,
  NextWebVitalsMetric,
} from "next/app";
import App from "next/app";
import type {
  NextComponentType,
  NextPageContext,
} from "next/dist/shared/lib/utils";
import type { ScriptProps } from "next/script";
import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  ReactNode,
  ComponentType,
} from "react";
import { RelayEnvironmentProvider } from "react-relay";

import { GQLEnumResourceTypeEnum } from "@framework/__generated__/graphql/enums";
import {
  GlobalDataContainerQuery,
  default as GlobalDataContainerQueryNode,
} from "@framework/__generated__/relay/GlobalDataContainerQuery.graphql";
import Admiral from "@framework/ads/components/Admiral";
import LiveRamp from "@framework/ads/components/LiveRamp";
import AnalyticsClient from "@framework/analytics/analytics-client";
import Analytics from "@framework/analytics/components/Analytics";
import CWVClient from "@framework/analytics/cwv-client";
import Volume from "@framework/analytics/volume-client";
import { getCommentProviderComponent } from "@framework/comments/get-comment-provider-component";
import { resolveCommentsConfigForApp } from "@framework/comments/resolve-comments-config-for-app";
import StickyNavCommentCount from "@framework/comments/StickyNavCommentCount";
import ChromeFixUnstyledTransitions from "@framework/components/app/ChromeFixUnsyledTransitions";
import CommunityTheme from "@framework/components/app/CommunityTheme";
import Favicons from "@framework/components/app/Favicons";
import Fonts, { DuetFonts } from "@framework/components/app/Fonts";
import { type SystemThemeMap } from "@framework/components/app/ThemeProvider";
import WikiBanner from "@framework/components/app/WikiBanner";
import LoopNavProviderContainer, {
  type LoopNavConfig,
} from "@framework/components/navigation/LoopNavProviderContainer";
import { appConfig } from "@framework/config/app.config";
import {
  NETWORK_COMMUNITY_DATA,
  getCommunityPrimaryCycloneCommunityId,
} from "@framework/config/community/communities";
import { getCommunityConfig } from "@framework/config/community/community.config";
import { coralConfig } from "@framework/config/coral.config";
import { crossPostConfigForCommunity } from "@framework/config/cross-post.config";
import { cycloneConfig } from "@framework/config/cyclone.config";
import { memberfulConfig as subscriptionProviderConfig } from "@framework/config/memberful.config";
import { NETWORK_ID, networkConfig } from "@framework/config/network.config";
import { DefaultSEOSchemaDefinition } from "@framework/config/seo.config";
import CycloneProvider from "@framework/cyclone/providers/CycloneProvider";
import DummyCycloneProvider from "@framework/cyclone/providers/DummyCycloneProvider";
import SubX from "@framework/entitlements/SubX";
import GlobalDataContainer, {
  type SiteSettingsOverride,
} from "@framework/global-data/GlobalDataContainer";
import useRelayEnvironment from "@framework/hooks/use-relay-environment";
import createQueryFetcher from "@framework/relay/internal/create-isomorphic-query-fetcher";
import { setQueryDurationHeaders } from "@framework/relay/server/query/query-fetcher.utils";
import { HydrationData } from "@framework/relay/shared/hydration";
import RouteInfoProvider from "@framework/route/RouteInfoProvider";
import CommunityDefaultSEO from "@framework/seo/components/CommunityDefaultSEO";
import ConfiguredSEOSchemas, {
  SEOSchemaConfigType,
} from "@framework/seo/components/ConfiguredSEOSchemas";
import { type DuetPageProps, type RelayProps } from "@framework/types/pages";
import { setCacheHeaders } from "@framework/utils/cache.utils";
import {
  parseIsPaywallEligibleHeader,
  parseResourceTypeHeader,
  safeParseCycloneCommunityIdHeader,
} from "@framework/utils/header.utils";
import {
  AdConfig,
  CommentsBrandConfig,
  CycloneConfig,
  FeatureConfig,
  MembershipConfig,
  NewslettersConfig,
  ParselyConfig,
  PaywallConfig,
  RetailerLinkOrderConfig,
  SeoConfig,
  StnVideoConfig,
  SubscriptionsConfig,
  UserProfileConfig,
  WunderkindConfig,
} from "@voxmedia/duet-context/context/config-context";
import { NavItem } from "@voxmedia/duet-context/context/global-data-context";
import { ServerAuthState } from "@voxmedia/duet-context/context/server-auth-context";
import AnalyticsProvider from "@voxmedia/duet-context/providers/AnalyticsProvider";
import CampaignProvider from "@voxmedia/duet-context/providers/CampaignProvider";
import CategoriesProvider from "@voxmedia/duet-context/providers/CategoriesProvider";
import CommunityProvider from "@voxmedia/duet-context/providers/CommunityProvider";
import ConfigProvider from "@voxmedia/duet-context/providers/ConfigProvider";
import DefaultCookieConsentProvider from "@voxmedia/duet-context/providers/CookieConsentProvider";
import LoopNavProvider from "@voxmedia/duet-context/providers/LoopNavProvider";
import PaywallProvider from "@voxmedia/duet-context/providers/PaywallProvider";
import Ad from "@voxmedia/duet-ui/components/ad/Ad";
import BreakingNewsBanner from "@voxmedia/duet-ui/components/cta/BreakingNewsBanner";
import SkipToContent from "@voxmedia/duet-ui/components/cta/SkipToContent";
import Navigation from "@voxmedia/duet-ui/components/navigation/Navigation";
import SELECTORS from "@voxmedia/duet-ui/config/class-selectors.config";
import { ComponentConfigs } from "@voxmedia/duet-ui/context/component-config-context";
import GracefulErrorBoundary from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import ComponentConfigProvider from "@voxmedia/duet-ui/providers/ComponentConfigProvider";
import NavigationProvider from "@voxmedia/duet-ui/providers/NavigationProvider";
import { sharedStyles } from "@voxmedia/duet-ui/shared-styles";
import {
  type CommunityThemeConfig,
  type Theme,
} from "@voxmedia/duet-ui/utils/theme.utils";
import {
  getExecutionDuration,
  isomorphicTimer,
} from "@voxmedia/duet-utils/logging";
import { RouteInfo, getRouteInfo } from "@voxmedia/duet-utils/route";
import { getCurrentOrigin, isPreviewPath } from "@voxmedia/duet-utils/url";

const GOOGLE_TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_ID;

// Relay core web vitals to analytics client
const client = GOOGLE_TAG_ID && new CWVClient();
export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (client) {
    client.emitMetric(metric);
  }
}

interface AdditionalNavMetadata {
  tagline?: string;
}

interface GlobalOverrides {
  mainNavItems?: NavItem[];
  footerItems?: NavItem[];
  siteSettings?: SiteSettingsOverride;
}

export interface DuetAppInitialProps extends AppInitialProps, RelayProps {
  additionalNavMetadata?: AdditionalNavMetadata;
  commentRecThreshold?: number;
  commentCalloutThreshold?: number;
  communityId: number;
  cycloneCommunityId: string;
  communityThemeConfig?: CommunityThemeConfig;
  currentOrigin: string;
  defaultTheme: Theme;
  themeStorageKey?: string;
  systemThemeMap?: SystemThemeMap;
  persistQueryTheme?: boolean;
  isPaywallEligible: boolean;
  preview?: boolean;
  routeInfo: RouteInfo;
  scripts?: ScriptProps[];
  serverAuthState?: ServerAuthState;
  subXTrackingEnabled?: boolean;
  themes: Record<string, string>;
  version?: string;
  loopNavConfig?: LoopNavConfig;
}

export interface DuetAppProps extends DuetAppInitialProps {
  adConfig: AdConfig;
  AppContentWrapper?: ComponentType<{ children: ReactNode }>;
  AuthProvider: ComponentType<{ children: ReactNode }>;
  /**
   * Optional replacement for the default OneTrust cookie-consent provider.
   * Brands that omit this keep `CookieConsentProvider` unchanged.
   */
  CookieConsentProvider?: ComponentType<{ children: ReactNode }>;
  Component: NextComponentType<NextPageContext, any, any>;
  componentConfig: ComponentConfigs;
  /** Brand-level comments defaults; resolved per request inside DuetApp. */
  commentsConfig?: CommentsBrandConfig;
  customArticleFooter?: ReactNode;
  defaultSEO: DefaultSEOSchemaDefinition;
  Favicons?: ReactNode;
  fonts?: DuetFonts;
  Footer: ReactNode;
  globalOverrides?: GlobalOverrides;
  Navigation?: ReactNode;
  newslettersConfig: NewslettersConfig;
  subscriptionsConfig?: SubscriptionsConfig;
  pageProps: DuetPageProps;
  parselyConfig?: ParselyConfig;
  paywallConfig?: PaywallConfig;
  seoConfig?: SeoConfig;
  seoSchemaConfigPathMap?: SEOSchemaConfigType;
  featureConfig?: FeatureConfig;
  membershipConfig?: MembershipConfig;
  stnVideoConfig?: StnVideoConfig;
  useDefaultNav?: boolean;
  userProfileConfig: UserProfileConfig;
  wunderkindConfig?: WunderkindConfig;
  retailerLinkOrderConfig?: RetailerLinkOrderConfig;
  loopNavConfig?: LoopNavConfig;
  /**
   * Temporary: network-supplied cyclone comment UI until moved into duet-framework.
   * SB Nation passes these; other networks omit them and stay on Coral.
   */
  CycloneArticleBody?: CycloneConfig["CycloneArticleBody"];
  CycloneCommentsSection?: CycloneConfig["CycloneCommentsSection"];
}

// Needs to be defined here to avoid rerender!
const defaultSEOConfig: SeoConfig = {
  pagesCanonicalMap: {},
  defaultOgImage: "",
};

const defaultParselyConfig: ParselyConfig = {
  tagsToInverse: [],
  enableTraining: false,
  enableArticleRecirc: false,
};

const defaultPaywallConfig = {
  isWalled: false,
  subXTrackingEnabled: false,
};

export const defaultFeatureConfig: FeatureConfig = {
  useLiveStreamUpdates: false,
  useQuickPosts: false,
  subcategoryLimit: 8,
  breadcrumbStrategy: "hierarchy",
  useBoostedPosts: false,
  useSaveVenuesToLists: false,
};

const defaultUserProfileConfig: UserProfileConfig = {
  requireCommentingName: false,
  commentingNameLabel: "commenting name",
};

const defaultMembershipConfig: MembershipConfig = {};

const defaultWunderkindConfig: WunderkindConfig = {};

const defaultCommentsConfigInput: CommentsBrandConfig = {
  defaultLocation: "drawer",
};

const defaultLoopNavConfig: LoopNavConfig = {};

function DuetApp(props: DuetAppProps) {
  // Memoize all props, so that they won't change!
  // NextJS even though we don't use the `<Link>` component
  // Still triggers routing functionality when encountering #hash
  // in the domain.
  //
  // When this happens, this whole tree gets rerendered, the
  // objects in the prop also appear to loose their reference,
  // it causes a lot of unmounts in the React Tree, so to avoid this
  // from happening, we just memoize the props and don'T allow them to change.

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedProps = useMemo(() => props, []);
  const {
    adConfig,
    additionalNavMetadata,
    AppContentWrapper = Fragment,
    AuthProvider,
    CookieConsentProvider = DefaultCookieConsentProvider,
    commentRecThreshold = 5,
    commentCalloutThreshold = 75,
    communityId,
    commentsConfig: commentsConfigInput = defaultCommentsConfigInput,
    CycloneArticleBody,
    CycloneCommentsSection,
    cycloneCommunityId,
    communityThemeConfig,
    Component,
    componentConfig,
    currentOrigin,
    customArticleFooter,
    defaultSEO,
    Favicons: CustomFavicons,
    featureConfig = defaultFeatureConfig,
    fonts,
    Footer,
    globalOverrides,
    hydration,
    isPaywallEligible,
    membershipConfig = defaultMembershipConfig,
    newslettersConfig,
    subscriptionsConfig,
    pageProps,
    parselyConfig = defaultParselyConfig,
    paywallConfig = defaultPaywallConfig,
    preview,
    routeInfo,
    seoConfig = defaultSEOConfig,
    seoSchemaConfigPathMap: seoSchemaConfig,
    serverAuthState,
    stnVideoConfig,
    token,
    userProfileConfig = defaultUserProfileConfig,
    wunderkindConfig = defaultWunderkindConfig,
    retailerLinkOrderConfig,
    loopNavConfig = defaultLoopNavConfig,
  } = memoizedProps;

  /**
   * Temporary service worker to block ad requests for domains to which requests are failing
   * which is causing slow page loads.
   */
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/ad-block-service-worker.js")
        .catch((err) =>
          captureException(err, {
            extra: {
              message: "Ad block service worker registration failed",
            },
          }),
        );
    }
  }, []);

  useEffect(() => {
    // This avoids NextJS router to take over when navigating from a url with #hash to the same url without.
    window.history.replaceState(null, "", window.location.toString());
  }, []);

  const analyticsClient = useMemo(() => new AnalyticsClient(), []);
  const {
    nodeId,
    appContainerClassName = "",
    navProps = {},
    hideNav,
    hideFooter,
    isMobileAppRequest,
    campaign = null,
    categories = null,
    internalCategories = null,
    primaryCategoryNodeId,
    primarySuperCatNodeId,
    resourceType,
    showBreakingNewsBanner,
    showWikiBanner,
    showLeaderboardTopAd,
  } = pageProps;

  const communityConfig = useMemo(
    () => getCommunityConfig(communityId),
    [communityId],
  );

  // if cyclone is enabled, then we can use the real CycloneProvider
  // otherwise, use the DummyCycloneProvider that just passes through the children
  // so that the provider doesn't error for networks not configured with cyclone.
  // Cannot add just in SBNation because the CycloneProvider needs access to ConfigProvider.
  const CycloneProviderToUse = cycloneConfig.enabled
    ? CycloneProvider
    : DummyCycloneProvider;

  // Run once on initial render.
  useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setContext("Community Config", communityConfig as any);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setContext("Feature Flags", featureConfig as any);
    setContext(
      "Page Props",
      omit(
        pageProps,
        // Omit graphql responses because they are large.
        "hydration",
        // Remove GraphQL API token if provided.
        "token",
        // Remove most popular articles data.
        "mostPopularArticles",
        // Omit sentry meta data
        "_sentryBaggage",
        "_sentryTraceData",
      ),
    );
    if (props.version) {
      setTag("version", props.version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hydrationCombined = useMemo<HydrationData>(
    () => ({
      responses: [
        ...hydration.responses,
        ...(pageProps.hydration ? pageProps.hydration.responses : []),
      ],
    }),
    [hydration.responses, pageProps.hydration],
  );

  const environment = useRelayEnvironment(
    hydrationCombined,
    token || pageProps.token,
    communityConfig,
  );

  const crossPostConfig = useMemo(
    () => crossPostConfigForCommunity(communityConfig.communityName),
    [communityConfig],
  );

  const resolvedCommentsConfig = useMemo(
    () =>
      resolveCommentsConfigForApp({
        commentsConfig: commentsConfigInput,
        communityId,
        isMobileApp: isMobileAppRequest,
      }),
    [commentsConfigInput, communityId, isMobileAppRequest],
  );

  const CommentProviderComponent = useMemo(
    () => getCommentProviderComponent(resolvedCommentsConfig.provider),
    [resolvedCommentsConfig.provider],
  );

  const ProvidersTreeMemoized = useMemo(() => {
    const ProvidersTree: FunctionComponent<{ children: React.ReactNode }> = (
      props,
    ) => {
      const networkCommunityData = NETWORK_COMMUNITY_DATA[NETWORK_ID];

      if (!networkCommunityData) {
        throw new Error("Community data for network not found");
      }

      return (
        // RelayEnvironmentProvider should be top level so everything has access to Relay
        <RelayEnvironmentProvider environment={environment}>
          <CommentProviderComponent>
            <GlobalDataContainer overrides={globalOverrides}>
              <CommunityDefaultSEO
                schema={defaultSEO}
                communityConfig={communityConfig}
              />
              <AuthProvider>
                <ConfigProvider
                  adConfig={adConfig}
                  appConfig={appConfig}
                  commentsConfig={resolvedCommentsConfig}
                  communityConfig={communityConfig}
                  coralConfig={{
                    ...coralConfig,
                    commentRecThreshold,
                    commentCalloutThreshold,
                  }}
                  cycloneConfig={{
                    ...cycloneConfig,
                    commentRecThreshold,
                    CycloneArticleBody,
                    CycloneCommentsSection,
                  }}
                  crossPostConfig={crossPostConfig}
                  customArticleFooter={customArticleFooter}
                  featureConfig={featureConfig}
                  membershipConfig={membershipConfig}
                  networkConfig={networkConfig}
                  newslettersConfig={newslettersConfig}
                  subscriptionsConfig={subscriptionsConfig}
                  parselyConfig={parselyConfig}
                  paywallConfig={paywallConfig}
                  seoConfig={seoConfig}
                  stnVideoConfig={stnVideoConfig}
                  subscriptionProviderConfig={subscriptionProviderConfig}
                  userProfileConfig={userProfileConfig}
                  wunderkindConfig={wunderkindConfig}
                  retailerLinkOrderConfig={retailerLinkOrderConfig}
                >
                  <RouteInfoProvider routeInfo={routeInfo}>
                    <CommunityProvider
                      currentOrigin={currentOrigin}
                      currentCommunityId={communityId}
                      currentCycloneCommunityId={cycloneCommunityId}
                      currentWpSiteId={communityConfig.communityWpSiteId}
                      communities={networkCommunityData.communities}
                    >
                      <CycloneProviderToUse>
                        <CampaignProvider campaign={campaign}>
                          <ComponentConfigProvider
                            componentConfig={componentConfig}
                          >
                            <CookieConsentProvider>
                              {/* Campaign data from CampaignProvider should be available to NavigationProvider */}
                              <NavigationProvider>
                                <CategoriesProvider
                                  categories={categories}
                                  internalCategories={internalCategories}
                                  primaryCategoryNodeId={primaryCategoryNodeId}
                                  primarySuperCatNodeId={primarySuperCatNodeId}
                                  superCatNodeId={
                                    resourceType ===
                                    GQLEnumResourceTypeEnum.superCat
                                      ? nodeId
                                      : undefined
                                  }
                                  categoryNodeId={
                                    resourceType ===
                                    GQLEnumResourceTypeEnum.category
                                      ? nodeId
                                      : undefined
                                  }
                                >
                                  <PaywallProvider
                                    isPaywallEligible={isPaywallEligible}
                                    paywallConfig={paywallConfig}
                                    trackingScript={
                                      <SubX
                                        isPaywallEligible={isPaywallEligible}
                                        paywallConfig={paywallConfig}
                                      />
                                    }
                                  >
                                    <CommunityTheme
                                      communityThemeConfig={
                                        communityThemeConfig
                                      }
                                    >
                                      {navProps.loopNav?.enable && nodeId ? (
                                        <LoopNavProviderContainer
                                          nodeId={nodeId}
                                          loopNavConfig={loopNavConfig}
                                        >
                                          {props.children}
                                        </LoopNavProviderContainer>
                                      ) : (
                                        /**
                                         * Providing context even when loop nav isn't used to ensure proper mocking
                                         * in tests and storybook by throwing errors when provider isn't present.
                                         */
                                        <LoopNavProvider loopNavData={null}>
                                          {props.children}
                                        </LoopNavProvider>
                                      )}
                                    </CommunityTheme>
                                  </PaywallProvider>
                                </CategoriesProvider>
                              </NavigationProvider>
                            </CookieConsentProvider>
                          </ComponentConfigProvider>
                        </CampaignProvider>
                      </CycloneProviderToUse>
                    </CommunityProvider>
                  </RouteInfoProvider>
                </ConfigProvider>
              </AuthProvider>
            </GlobalDataContainer>
          </CommentProviderComponent>
        </RelayEnvironmentProvider>
      );
    };
    return ProvidersTree;
  }, [
    CommentProviderComponent,
    resolvedCommentsConfig,
    environment,
    globalOverrides,
    defaultSEO,
    communityConfig,
    AuthProvider,
    CookieConsentProvider,
    adConfig,
    commentRecThreshold,
    commentCalloutThreshold,
    CycloneArticleBody,
    CycloneCommentsSection,
    crossPostConfig,
    customArticleFooter,
    featureConfig,
    membershipConfig,
    newslettersConfig,
    subscriptionsConfig,
    parselyConfig,
    paywallConfig,
    seoConfig,
    stnVideoConfig,
    userProfileConfig,
    wunderkindConfig,
    retailerLinkOrderConfig,
    routeInfo,
    currentOrigin,
    communityId,
    cycloneCommunityId,
    CycloneProviderToUse,
    campaign,
    componentConfig,
    categories,
    internalCategories,
    primaryCategoryNodeId,
    primarySuperCatNodeId,
    resourceType,
    nodeId,
    isPaywallEligible,
    communityThemeConfig,
    navProps.loopNav?.enable,
    loopNavConfig,
  ]);

  const renderStickyNavCommentCount = useMemo(() => {
    const meta = navProps.stickyNavCommentMeta;
    if (!meta) {
      return undefined;
    }

    const { nodeArgs, url, authorName, headline, contentType } = meta;

    function renderStickyNavCommentCount(className: string) {
      return (
        <StickyNavCommentCount
          nodeArgs={nodeArgs}
          url={url}
          authorName={authorName}
          headline={headline}
          contentType={contentType}
          className={className}
        />
      );
    }

    return renderStickyNavCommentCount;
  }, [navProps.stickyNavCommentMeta]);

  let element = (
    <>
      <Fonts fonts={fonts} />
      <Admiral
        communityId={communityId}
        communityName={communityConfig.communityName}
        deployEnv={appConfig.deployEnv}
        networkId={networkConfig.networkId}
      />
      <ChromeFixUnstyledTransitions />
      <ProvidersTreeMemoized>
        <AppContentWrapper>
          <div
            className={clsx(
              SELECTORS.AppContainer,
              navProps.variant !== "minimal" &&
                sharedStyles.layout.flexAppContainer,
              appContainerClassName,
            )}
          >
            {!hideNav && (
              <>
                <SkipToContent anchorId="content" />
                {showLeaderboardTopAd && <Ad adType="leaderboardTop" />}
                {showBreakingNewsBanner && <BreakingNewsBanner />}
                <Navigation
                  variant={navProps.variant || "standard"}
                  stickyNav={navProps.stickyNav}
                  renderStickyNavCommentCount={renderStickyNavCommentCount}
                  getDrawerComponents={navProps.getDrawerComponents}
                  tagline={additionalNavMetadata?.tagline}
                />
                {showWikiBanner && !serverAuthState?.isEntitled && (
                  <WikiBanner template={navProps.template} />
                )}
              </>
            )}
            {/** ConfiguredSEOSchemas must be a child of RouteInfoProvider */}
            <ConfiguredSEOSchemas seoSchemaConfig={seoSchemaConfig} />
            <LiveRamp />
            {CustomFavicons ? CustomFavicons : <Favicons />}
            <Component {...pageProps} />
            {hideFooter ? null : Footer}
          </div>
        </AppContentWrapper>
      </ProvidersTreeMemoized>
    </>
  );

  if (process.env.NODE_ENV === "production") {
    // in production, wrap this around a very forgiving error boundary, that logs to Sentry.
    element = (
      <GracefulErrorBoundary context="AppErrorBoundary" layout="App">
        {element}
      </GracefulErrorBoundary>
    );
  }

  // wrap in `AnalyticsProvider` when `NEXT_PUBLIC_GOOGLE_TAG_ID` is provided.
  if (GOOGLE_TAG_ID && !preview && !isMobileAppRequest) {
    return (
      <>
        <Analytics
          gtmId={GOOGLE_TAG_ID}
          communityName={communityConfig.communityName}
          nodeId={nodeId}
          parselyHeadlineTestingApiKey={parselyConfig.headlineTestingApiKey}
        />
        <AnalyticsProvider
          analyticsClient={analyticsClient}
          volume={Volume}
          subXTrackingEnabled={!!paywallConfig.subXTrackingEnabled}
        >
          {element}
        </AnalyticsProvider>
      </>
    );
  }
  return element;
}

export default DuetApp;

DuetApp.getInitialProps = async ({
  additionalNavMetadata,
  context,
  themes,
  defaultTheme,
  themeStorageKey,
  systemThemeMap,
  persistQueryTheme,
  scripts,
  serverAuthState,
  commentRecThreshold,
  commentCalloutThreshold,
  communityId,
  communityThemeConfig,
  loopNavConfig,
}: {
  additionalNavMetadata?: AdditionalNavMetadata;
  context: AppContext;
  themes: Record<string, string>;
  defaultTheme: Theme;
  themeStorageKey?: string;
  systemThemeMap?: SystemThemeMap;
  persistQueryTheme?: boolean;
  scripts?: ScriptProps[];
  serverAuthState?: ServerAuthState;
  commentRecThreshold?: number;
  commentCalloutThreshold?: number;
  communityId: number;
  communityThemeConfig?: CommunityThemeConfig;
  loopNavConfig?: LoopNavConfig;
  primaryCategoryNodeId?: string;
}): Promise<DuetAppInitialProps> => {
  const gipTimer = isomorphicTimer();
  const ctx = await App.getInitialProps(context);
  const { req, res, asPath } = context.ctx;

  const routeInfo: RouteInfo = asPath
    ? getRouteInfo(asPath)
    : {
        base: "",
        page: null,
      };

  if (routeInfo.base === "") {
    console.warn("RouteInfo not available");
  }

  if (!req) {
    throw new Error("Expected a request object");
  }

  const resourceType = parseResourceTypeHeader(req.headers);

  // Get the primary cyclone community ID as fallback for cases where middleware is skipped
  const primaryCycloneCommunityId =
    getCommunityPrimaryCycloneCommunityId(communityId);
  const cycloneCommunityId = safeParseCycloneCommunityIdHeader(
    req.headers,
    primaryCycloneCommunityId,
  );

  const preview = isPreviewPath(asPath);
  const communityConfig = getCommunityConfig(communityId);
  const { vmPrefix, isFeedOnlyCommunity, primaryCommunityId } = communityConfig;
  const globalQueryCommunityId: number =
    isFeedOnlyCommunity && primaryCommunityId
      ? primaryCommunityId
      : communityId;
  const queryFetcher = createQueryFetcher(req, globalQueryCommunityId);

  const queryResult = await queryFetcher.fetchQuery<GlobalDataContainerQuery>({
    query: GlobalDataContainerQueryNode,
    variables: {},
    durationHeaderKey: "vm-duration-global-data-query",
  });

  const isPaywallEligible = parseIsPaywallEligibleHeader(req.headers);

  if (res) {
    setQueryDurationHeaders(res, [queryResult]);
    setCacheHeaders({
      res,
      preview,
      vmPrefix,
      queryResponseKeys: queryResult.surrogateKeys,
    });

    res.setHeader("vm-duration-gip", getExecutionDuration(gipTimer));
    res.setHeader("vm-app-path", asPath || "");
  }

  return merge(
    {
      ...ctx,
      hydration: queryFetcher.hydration,
      preview,
      themes,
      themeStorageKey,
      systemThemeMap,
      persistQueryTheme,
      additionalNavMetadata,
      commentRecThreshold,
      commentCalloutThreshold,
      communityId,
      cycloneCommunityId,
      communityThemeConfig,
      loopNavConfig,
      defaultTheme,
      isPaywallEligible,
      routeInfo,
      scripts,
      serverAuthState,
      currentOrigin: getCurrentOrigin(req),
      // Since version is not bundled into the client, we'll pass it as a prop.
      version: process.env.VERSION,
    },
    {
      pageProps: {
        resourceType,
      },
    },
  );
};
